/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Building2, Users2, Globe2 } from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const SCOPES = [
    {
        icon: Building2,
        label: 'Agence indépendante',
        desc: 'Une équipe, un point de vente. Un cockpit dédié.',
    },
    {
        icon: Users2,
        label: 'Groupe d\'agences',
        desc: 'Plusieurs agences pilotées dans une vue consolidée.',
    },
    {
        icon: Globe2,
        label: 'Réseau / Franchise',
        desc: 'Animation de réseau, qualité multi-locale, marque collective.',
    },
];

/**
 * ConvDiscoverPlan — Iter169.
 *
 * Remplace l'ancienne section Pricing publique. Décision commerciale :
 * pas d'affichage de tarif sur la landing — l'objectif est de maximiser
 * les demandes entrantes de RDV. Le prix est révélé en démo, après
 * qualification.
 *
 * Cette section pousse vers la prise de contact tout en signalant que
 * Homly s'adapte à chaque taille d'organisation.
 */
export const ConvDiscoverPlan = ({ onDemoClick }) => {
    return (
        <section className="relative py-20 md:py-32" data-testid="discover-plan-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="text-center max-w-3xl mx-auto">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.5 }}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[10px] font-medium tracking-widest text-[#A19DAB] uppercase"
                        style={MONO}
                    >
                        <Sparkles className="w-3 h-3 text-[#FF914D]" />
                        Une offre pensée pour votre organisation
                    </motion.span>

                    <motion.h2
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.6, delay: 0.05 }}
                        className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.1]"
                        style={DISPLAY}
                    >
                        Discutons de votre{' '}
                        <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                            besoin.
                        </span>
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.6, delay: 0.12 }}
                        className="mt-5 text-base md:text-lg text-[#A19DAB] leading-relaxed"
                    >
                        Chaque agence a son contexte, son équipe et ses objectifs.
                        Nous construisons ensemble la formule la plus adaptée à votre
                        organisation — agence indépendante, groupe ou réseau.
                    </motion.p>
                </div>

                {/* Scope chips */}
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                    {SCOPES.map(({ icon: Icon, label, desc }, i) => (
                        <motion.div
                            key={label}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.5, delay: 0.18 + i * 0.08 }}
                            className="group relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition-all duration-500 hover:border-white/25 hover:bg-white/[0.04] hover:-translate-y-1"
                            data-testid={`scope-card-${i}`}
                        >
                            <div className="w-11 h-11 rounded-xl bg-[#A855F7]/12 flex items-center justify-center mb-4 transition-transform duration-500 group-hover:scale-110">
                                <Icon className="w-5 h-5 text-[#C7A1F0]" />
                            </div>
                            <h3 className="text-base font-semibold text-white mb-1.5" style={DISPLAY}>
                                {label}
                            </h3>
                            <p className="text-sm text-[#A19DAB] leading-relaxed">{desc}</p>
                        </motion.div>
                    ))}
                </div>

                {/* CTA bloc central */}
                <motion.div
                    initial={{ opacity: 0, y: 22 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="relative mt-14 rounded-3xl border border-[#681793]/30 bg-gradient-to-br from-[#681793]/15 via-[#0C0A10] to-[#FF914D]/10 p-8 md:p-12 overflow-hidden"
                    data-testid="discover-plan-cta-block"
                >
                    <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#681793]/25 blur-[110px]" />
                    <div className="pointer-events-none absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[#FF914D]/15 blur-[110px]" />

                    <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div className="max-w-xl">
                            <h3 className="text-2xl md:text-3xl font-bold tracking-tighter text-white leading-[1.15]" style={DISPLAY}>
                                Une démonstration personnalisée,{' '}
                                <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                                    en 20 minutes.
                                </span>
                            </h3>
                            <p className="mt-3 text-sm md:text-base text-[#A19DAB] leading-relaxed">
                                Pas d'argumentaire automatique. On regarde ensemble votre
                                organisation, vos enjeux, et ce que Homly peut concrètement
                                apporter à vos équipes.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onDemoClick}
                            data-testid="discover-plan-demo-btn"
                            className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-semibold px-7 py-3.5 rounded-full shadow-xl shadow-[#681793]/30 hover:opacity-95 hover:scale-[1.02] transition-all flex-shrink-0"
                        >
                            Demander une démonstration
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                        </button>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
